import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Topbar from "../component/Topbar";
import Sidebar from "../component/Sidebar";
import Footer from "../component/Footer";
import MobileFilter from "../component/MobileFilter";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";

const parseQuizOptions = (item) => {
  if (!item || typeof item !== "object") return [];

  const extractText = (val) => {
    if (val == null) return "";
    if (typeof val === "string" || typeof val === "number") return String(val).trim();
    if (typeof val === "object") {
      return String(
        val.option_text ??
        val.text ??
        val.optionText ??
        val.option_name ??
        val.optionName ??
        val.option_value ??
        val.optionValue ??
        val.option_desc ??
        val.title ??
        val.value ??
        val.desc ??
        val.description ??
        val.answer_text ??
        val.answerText ??
        val.answer ??
        val.ans ??
        val.choice_text ??
        val.choiceText ??
        val.choice ??
        (typeof val.option === "string" && val.option.length > 2 ? val.option : "") ??
        val.name ??
        ""
      ).trim();
    }
    return "";
  };

  const extractLabel = (val, defaultLabel) => {
    if (!val || typeof val !== "object") return defaultLabel;
    const rawLabel =
      val.label ??
      val.key ??
      val.code ??
      val.option_no ??
      val.optionNo ??
      val.option_key ??
      val.optionKey ??
      val.option_label ??
      val.optionLabel ??
      val.option_code ??
      val.choice ??
      val.ans_key ??
      (typeof val.option === "string" && val.option.length <= 2 ? val.option : null) ??
      defaultLabel;
    const clean = String(rawLabel).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return clean || defaultLabel;
  };

  // 1. Direct candidates on item
  let candidate =
    item.options ??
    item.choices ??
    item.answers ??
    item.option_list ??
    item.options_list ??
    item.question_options ??
    item.quiz_options ??
    item.all_options ??
    item.question?.options ??
    item.question_data?.options ??
    item.details?.options;

  // JSON string or comma-separated string
  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        candidate = JSON.parse(trimmed);
      } catch (e) {
        // continue
      }
    } else if (trimmed.includes(",")) {
      candidate = trimmed.split(",").map((s) => s.trim());
    }
  }

  // 1a. Array
  if (Array.isArray(candidate) && candidate.length > 0) {
    const list = candidate
      .map((opt, idx) => {
        const defaultLabel = String.fromCharCode(65 + idx);
        if (typeof opt === "string" || typeof opt === "number") {
          const str = String(opt).trim();
          const match = str.match(/^([A-Da-d0-9])[.\-):]\s*(.*)$/);
          if (match) {
            return {
              label: match[1].toUpperCase(),
              text: match[2].trim() || str,
            };
          }
          return { label: defaultLabel, text: str };
        }
        if (opt && typeof opt === "object") {
          const lbl = extractLabel(opt, defaultLabel);
          const txt = extractText(opt);
          if (txt) {
            return { label: lbl, text: txt };
          }
        }
        return null;
      })
      .filter((o) => o && Boolean(o.text));

    if (list.length > 0) return list;
  }

  // 1b. Object
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    const entries = Object.entries(candidate);
    if (entries.length > 0) {
      const list = entries
        .map(([k, val], idx) => {
          let lbl = k.replace(/^(option_|opt_|choice_|ans_|answer_)/i, "").toUpperCase();
          if (!isNaN(lbl)) {
            lbl = String.fromCharCode(65 + parseInt(lbl, 10) - (parseInt(lbl, 10) > 0 ? 1 : 0));
          }
          if (!lbl || lbl.length > 2) {
            lbl = String.fromCharCode(65 + idx);
          }
          const txt = extractText(val);
          return txt ? { label: lbl, text: txt } : null;
        })
        .filter((o) => o && Boolean(o.text));

      if (list.length > 0) return list;
    }
  }

  // 2. Flat keys on item: option_a, option_b, option_c, option_d, etc.
  const flatGroups = [
    {
      label: "A",
      keys: [
        "option_a", "optionA", "Option_A", "OptionA",
        "option_1", "option1", "Option_1", "Option1",
        "opt_a", "optA", "Opt_A", "OptA",
        "opt_1", "opt1",
        "choice_a", "choiceA", "Choice_A", "ChoiceA",
        "choice_1", "choice1",
        "ans_a", "ansA", "ans_1", "ans1",
        "answer_a", "answerA", "answer_1", "answer1",
        "a", "A",
      ],
    },
    {
      label: "B",
      keys: [
        "option_b", "optionB", "Option_B", "OptionB",
        "option_2", "option2", "Option_2", "Option2",
        "opt_b", "optB", "Opt_B", "OptB",
        "opt_2", "opt2",
        "choice_b", "choiceB", "Choice_B", "ChoiceB",
        "choice_2", "choice2",
        "ans_b", "ansB", "ans_2", "ans2",
        "answer_b", "answerB", "answer_2", "answer2",
        "b", "B",
      ],
    },
    {
      label: "C",
      keys: [
        "option_c", "optionC", "Option_C", "OptionC",
        "option_3", "option3", "Option_3", "Option3",
        "opt_c", "optC", "Opt_C", "OptC",
        "opt_3", "opt3",
        "choice_c", "choiceC", "Choice_C", "ChoiceC",
        "choice_3", "choice3",
        "ans_c", "ansC", "ans_3", "ans3",
        "answer_c", "answerC", "answer_3", "answer3",
        "c", "C",
      ],
    },
    {
      label: "D",
      keys: [
        "option_d", "optionD", "Option_D", "OptionD",
        "option_4", "option4", "Option_4", "Option4",
        "opt_d", "optD", "Opt_D", "OptD",
        "opt_4", "opt4",
        "choice_d", "choiceD", "Choice_D", "ChoiceD",
        "choice_4", "choice4",
        "ans_d", "ansD", "ans_4", "ans4",
        "answer_d", "answerD", "answer_4", "answer4",
        "d", "D",
      ],
    },
    {
      label: "E",
      keys: [
        "option_e", "optionE", "Option_E", "OptionE",
        "option_5", "option5",
        "opt_e", "optE",
        "choice_e", "choiceE",
        "ans_e", "ansE",
        "answer_e", "answerE",
        "e", "E",
      ],
    },
  ];

  const matched = [];
  for (const group of flatGroups) {
    for (const key of group.keys) {
      if (
        item[key] != null &&
        item[key] !== "" &&
        typeof item[key] !== "object" &&
        (key.length > 1 || String(item[key]).length > 1)
      ) {
        matched.push({ label: group.label, text: String(item[key]).trim() });
        break;
      }
    }
  }

  if (matched.length > 0) {
    return matched;
  }

  // 3. Dynamic search across all object entries for option-like keys
  const dynamicEntries = Object.entries(item).filter(([k, v]) => {
    return (
      /^(option|opt|choice|answer|ans)[_]?[a-e0-9]/i.test(k) &&
      v != null &&
      v !== "" &&
      typeof v !== "object" &&
      !/id|count|points|point|correct|status|result/i.test(k)
    );
  });

  if (dynamicEntries.length > 0) {
    return dynamicEntries.map(([k, v], idx) => {
      let lbl = k.replace(/^(option|opt|choice|answer|ans)[_]?/i, "").toUpperCase();
      if (!lbl || lbl.length > 2) lbl = String.fromCharCode(65 + idx);
      return {
        label: lbl,
        text: String(v).trim(),
      };
    });
  }

  return [];
};

const formatResultDateTime = (val) => {
  if (!val) return "";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return String(val);
  }
};

export default function PointDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const [resultHistoryData, setResultHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const fetchResultHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/account/learn_earn_result_history`, {
        headers: getApiHeaders(),
      });

      console.log("learn_earn_result_history response:", res?.data);
      if (res?.data) {
        const raw = res.data?.data || res.data;
        if (Array.isArray(raw)) {
          setResultHistoryData(raw);
        } else {
          setResultHistoryData([]);
        }
      } else {
        setResultHistoryData([]);
      }
    } catch (error) {
      console.error(
        "Error fetching learn_earn_result_history:",
        error?.response?.data || error.message || error
      );
      setResultHistoryData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResultHistory();
  }, [fetchResultHistory]);

  const getItemDateString = (item) => {
    const val = item.ondate || item.created_at || item.datetime || item.date;
    if (!val) return null;
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
      return val.slice(0, 10);
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return null;
  };

  // Front-end Search & Clear handlers
  const handleSearch = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setAppliedFromDate("");
    setAppliedToDate("");
  };

  const historyList = useMemo(() => {
    return Array.isArray(resultHistoryData) ? resultHistoryData : [];
  }, [resultHistoryData]);

  // Front-side date filtering
  const filteredList = useMemo(() => {
    return historyList.filter((item) => {
      if (appliedFromDate || appliedToDate) {
        const itemDateStr = getItemDateString(item);
        if (itemDateStr) {
          if (appliedFromDate && itemDateStr < appliedFromDate) return false;
          if (appliedToDate && itemDateStr > appliedToDate) return false;
        } else {
          return false;
        }
      }
      return true;
    });
  }, [historyList, appliedFromDate, appliedToDate]);

  // Statistics calculated from the active filteredList
  const stats = useMemo(() => {
    const list = filteredList;
    const total = list.length;
    const correctCount = list.filter(
      (item) => item.is_correct === true || String(item.result || "").toLowerCase() === "correct"
    ).length;
    const totalPoints = list.reduce(
      (sum, item) => sum + (Number(item.points_earned) || 0),
      0
    );
    const usdEstimate = (totalPoints / 500).toFixed(2);
    const potentialPoints = total * 10;
    const percentage = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    const levelName =
      historyList[0]?.level_name || location.state?.pointData?.levelName || "Basic";
    const levelNo =
      historyList[0]?.level_no || location.state?.pointData?.levelNo || 1;

    return {
      total,
      correctCount,
      totalPoints,
      usdEstimate,
      potentialPoints,
      percentage,
      levelName,
      levelNo,
    };
  }, [filteredList, historyList, location.state]);

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid mb-4">
            {/* Page Title */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <div>
                    <h2 className="fw-700">Quiz Result History</h2>
                    <p className="text-muted mb-sm-1 font-size-13">
                      Performance summary &amp; question-by-question review
                    </p>
                  </div>

                  <div className="page-title-right d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/learn-and-earn")}
                    >
                      <i className="bx bx-arrow-back me-2 fs-5"></i> Back to Learn &amp; Earn
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 4 Professional KPI Metric Cards */}
            <div className="row g-3 mb-4">
              {/* Card 1: Level */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="card card-animate overflow-hidden athos-stat h-100 mb-0 shadow-sm border-0">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="text-muted mb-0 fw-semibold text-uppercase font-size-12">
                        Current Level
                      </p>
                      <div className="avatar-xs rounded-circle athos-stat__icon d-flex align-items-center justify-content-center">
                        <i className="bx bxs-star font-size-16" />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-2 text-dark">
                      {stats.levelName}
                    </h3>
                    <div className="d-flex align-items-center gap-2 text-muted font-size-12">
                      <span className="badge bg-primary text-white px-2 py-0.5 rounded-pill me-1.5">
                        Level {stats.levelNo}
                      </span>
                      <span>Daily Learning Track</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Tasks Completed */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="card card-animate overflow-hidden athos-stat h-100 mb-0 shadow-sm border-0">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="text-muted mb-0 fw-semibold text-uppercase font-size-12">
                        Tasks Completed
                      </p>
                      <div className="avatar-xs rounded-circle athos-stat__icon d-flex align-items-center justify-content-center">
                        <i className="bx bx-calendar-check font-size-16" />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-1 text-dark">
                      {stats.total} Days
                    </h3>
                    <div className="d-flex align-items-center gap-2 text-muted font-size-12">
                      <span className="badge bg-info text-white px-2 py-0.5 rounded-pill me-1.5">
                        Quizzes
                      </span>
                      <span>{stats.total} questions answered</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Accuracy Score */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="card card-animate overflow-hidden athos-stat h-100 mb-0 shadow-sm border-0">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="text-muted mb-0 fw-semibold text-uppercase font-size-12">
                        Accuracy Rate
                      </p>
                      <div className="avatar-xs rounded-circle athos-stat__icon d-flex align-items-center justify-content-center">
                        <i className="bx bx-trophy font-size-16" />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-1 text-dark">
                      {stats.correctCount} / {stats.total}
                      <span className="font-size-14 text-muted fw-normal ms-1">
                        ({stats.percentage}%)
                      </span>
                    </h3>
                    <div className="d-flex align-items-center gap-2 text-muted font-size-12">
                      <span className="badge bg-success text-white px-2 py-0.5 rounded-pill me-1.5">
                        {stats.correctCount} Correct
                      </span>
                      <span>{stats.total - stats.correctCount} Incorrect</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Points Earned */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="card card-animate overflow-hidden athos-stat h-100 mb-0 shadow-sm border-0">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="text-muted mb-0 fw-semibold text-uppercase font-size-12">
                        Points Earned
                      </p>
                      <div className="avatar-xs rounded-circle athos-stat__icon d-flex align-items-center justify-content-center">
                        <i className="bx bxs-zap font-size-16" />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-1 text-success">
                      +{stats.totalPoints} Pts
                    </h3>
                    <div className="d-flex align-items-center gap-2 text-muted font-size-12">
                      <span className="badge bg-warning text-dark px-2 py-0.5 rounded-pill me-1.5 fw-semibold">
                        ≈ ${stats.usdEstimate} USD
                      </span>
                      <span>Max {stats.potentialPoints} Pts</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Date Filter Card - Desktop & Mobile */}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                  <div className="card-body">
                    {/* Desktop Filters */}
                    <div className="d-flex flex-wrap align-items-end gap-2 desktop-filter-row">
                      <div>
                        <label htmlFor="fromDatePointDetails" className="form-label mb-1">
                          From
                        </label>
                        <input
                          id="fromDatePointDetails"
                          className="form-control"
                          type="date"
                          name="fromDate"
                          max={toDate || today}
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>

                      <div>
                        <label htmlFor="toDatePointDetails" className="form-label mb-1">
                          To
                        </label>
                        <input
                          id="toDatePointDetails"
                          className="form-control"
                          type="date"
                          name="toDate"
                          min={fromDate || undefined}
                          max={today}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>

                      <div>
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light"
                          onClick={handleSearch}
                        >
                          Search
                        </button>
                      </div>

                      <div>
                        <button
                          type="button"
                          className="btn btn-light border waves-effect waves-light"
                          onClick={handleClear}
                          disabled={!fromDate && !toDate && !appliedFromDate && !appliedToDate}
                        >
                          Clear
                        </button>
                      </div>

                      {(appliedFromDate || appliedToDate) && (
                        <div className="ms-auto align-self-center">
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 font-size-12 rounded-pill">
                            Showing {filteredList.length} of {historyList.length} records
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Mobile Filters */}
                    <div className="d-block d-md-none">
                      <MobileFilter title="Quiz Result History">
                        <div>
                          <label htmlFor="fromDateMobile" className="form-label mb-1 font-size-13 fw-medium">
                            From Date
                          </label>
                          <input
                            id="fromDateMobile"
                            className="form-control"
                            type="date"
                            name="fromDate"
                            max={toDate || today}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>

                        <div>
                          <label htmlFor="toDateMobile" className="form-label mb-1 font-size-13 fw-medium">
                            To Date
                          </label>
                          <input
                            id="toDateMobile"
                            className="form-control"
                            type="date"
                            name="toDate"
                            min={fromDate || undefined}
                            max={today}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>

                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={handleSearch}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={handleClear}
                            disabled={!fromDate && !toDate && !appliedFromDate && !appliedToDate}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>

                      {(appliedFromDate || appliedToDate) && (
                        <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1 font-size-11 rounded-pill">
                            Filtered: {appliedFromDate || "Start"} to {appliedToDate || "End"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 font-size-12 text-decoration-none fw-semibold"
                            onClick={handleClear}
                          >
                            Clear Filter
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions Result List */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading result history...</span>
                </div>
                <div className="text-muted mt-2 font-size-14 fw-medium">
                  Loading quiz results...
                </div>
              </div>
            ) : filteredList.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {filteredList.map((item, index) => {
                  const isCorrect =
                    item.is_correct === true ||
                    String(item.result || "").toLowerCase() === "correct";
                  const pts = Number(item.points_earned) || 0;
                  const formattedDateTime = formatResultDateTime(item.ondate);
                  const parsedOptions = parseQuizOptions(item);

                  const userAns = String(
                    item.selected_option ||
                    item.user_answer ||
                    item.user_ans ||
                    item.submitted_option ||
                    ""
                  ).trim();

                  const correctAns = String(
                    item.correct_option ||
                    item.right_option ||
                    item.correct_answer ||
                    ""
                  ).trim();

                  return (
                    <div
                      key={item.id || item.question_id || index}
                      className="card border shadow-sm mb-0"
                      style={{
                        borderRadius: "12px",
                        backgroundColor: "var(--bs-card-bg, #ffffff)",
                        borderLeft: isCorrect
                          ? "5px solid #10b981"
                          : "5px solid #ef4444",
                        overflow: "hidden",
                      }}
                    >
                      <div className="card-body p-3 p-md-4">
                        {/* Question Card Header */}
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 pb-2 border-bottom">
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="badge bg-primary text-white font-size-12 px-2.5 py-1 fw-bold rounded-pill">
                              Day {item.day_no}
                            </span>
                            <span className="fw-bold font-size-13 text-dark">
                              Question #{item.question_id || index + 1}
                            </span>
                            {formattedDateTime && (
                              <span className="text-muted font-size-12 d-flex align-items-center gap-1 ms-1">
                                <i className="bx bx-time-five font-size-14 text-muted" />
                                <span>{formattedDateTime}</span>
                              </span>
                            )}
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={`badge rounded-pill fw-semibold font-size-12 px-3 py-1.5 ${isCorrect
                                ? "bg-success-subtle text-success border border-success-subtle"
                                : "bg-danger-subtle text-danger border border-danger-subtle"
                                }`}
                            >
                              {isCorrect ? (
                                <>
                                  <i className="bx bx-check-circle me-1" />
                                  Correct
                                </>
                              ) : (
                                <>
                                  <i className="bx bx-x-circle me-1" />
                                  Incorrect
                                </>
                              )}
                            </span>

                            <span
                              className={`badge rounded-pill fw-bold font-size-12 px-3 py-1.5 ${isCorrect
                                ? "bg-success text-white shadow-none"
                                : "bg-light text-muted border shadow-none"
                                }`}
                            >
                              {isCorrect ? `+${pts} Pts` : `+0 Pts`}
                            </span>
                          </div>
                        </div>

                        {/* Question Text */}
                        <h5
                          className="fw-semibold font-size-15 my-3 text-dark"
                          style={{ lineHeight: "1.5" }}
                        >
                          {item.question_text}
                        </h5>

                        {/* Options Section */}
                        {parsedOptions.length > 0 ? (
                          <div className="row g-3">
                            {parsedOptions.map((opt) => {
                              const optLabel = String(opt.label || "").trim().toUpperCase();
                              const optText = String(opt.text || "").trim().toLowerCase();

                              const userSelected =
                                userAns.toUpperCase() === optLabel ||
                                (optText && optText === userAns.toLowerCase()) ||
                                userAns.toUpperCase() === `OPTION ${optLabel}`;

                              const isCorrectAnswer =
                                correctAns.toUpperCase() === optLabel ||
                                (optText && optText === correctAns.toLowerCase()) ||
                                correctAns.toUpperCase() === `OPTION ${optLabel}` ||
                                (isCorrect && userSelected);

                              let cardBorder = "1px solid #e2e8f0";
                              let cardBg = "#ffffff";
                              let badgeEl = null;

                              if (isCorrectAnswer) {
                                cardBorder = "1.5px solid #10b981";
                                cardBg = "#ecfdf5";
                                badgeEl = (
                                  <span className="badge rounded-pill px-2.5 py-1 font-size-11 fw-bold bg-success text-white shadow-none d-flex align-items-center gap-1 flex-shrink-0">
                                    <i className="bx bx-check" />
                                    {userSelected
                                      ? "Your Answer (Correct)"
                                      : "Correct Answer"}
                                  </span>
                                );
                              } else if (userSelected && !isCorrect) {
                                cardBorder = "1.5px solid #ef4444";
                                cardBg = "#fef2f2";
                                badgeEl = (
                                  <span className="badge rounded-pill px-2.5 py-1 font-size-11 fw-bold bg-danger text-white shadow-none d-flex align-items-center gap-1 flex-shrink-0">
                                    <i className="bx bx-x" />
                                    Your Answer
                                  </span>
                                );
                              }

                              return (
                                <div key={optLabel} className="col-12 col-md-6">
                                  <div
                                    className="p-2 d-flex align-items-center justify-content-between rounded-3 transition-all h-100"
                                    style={{
                                      border: cardBorder,
                                      backgroundColor: cardBg,
                                      minHeight: "56px",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 min-w-0">
                                      <div
                                        className="d-flex align-items-center justify-content-center fw-bold rounded-circle flex-shrink-0"
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          backgroundColor: isCorrectAnswer
                                            ? "#10b981"
                                            : userSelected
                                              ? "#ef4444"
                                              : "#f1f5f9",
                                          color:
                                            isCorrectAnswer || userSelected
                                              ? "#ffffff"
                                              : "#475569",
                                          fontSize: "13px",
                                          border:
                                            isCorrectAnswer || userSelected
                                              ? "none"
                                              : "1px solid #cbd5e1",
                                        }}
                                      >
                                        {optLabel}
                                      </div>
                                      <span
                                        className="fw-medium font-size-14 text-dark text-break"
                                        style={{ lineHeight: "1.4" }}
                                      >
                                        {opt.text}
                                      </span>
                                    </div>

                                    {badgeEl}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          /* Fallback Answer Summary row when response didn't contain full option list */
                          <div className="p-3 bg-light rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border">
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted font-size-13 fw-medium">
                                Your Selection:
                              </span>
                              <span
                                className={`badge px-3 py-1.5 font-size-12 rounded-pill fw-bold ${isCorrect ? "bg-success text-white" : "bg-danger text-white"
                                  }`}
                              >
                                Option {userAns || "-"} {isCorrect ? "(Correct)" : "(Incorrect)"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted font-size-13 fw-medium">
                                Correct Answer:
                              </span>
                              <span className="badge bg-success-subtle text-success border border-success px-3 py-1.5 font-size-12 rounded-pill fw-bold">
                                Option {correctAns || userAns || "-"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="card border shadow-sm text-center py-5"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "var(--bs-card-bg, #ffffff)",
                }}
              >
                <div className="card-body">
                  <i className="bx bx-file-blank text-muted font-size-48 mb-2 d-block" />
                  <h5 className="fw-semibold text-muted mb-1">
                    No Result History Found
                  </h5>
                  <p className="text-muted font-size-13 mb-3">
                    {appliedFromDate || appliedToDate
                      ? "No quiz result records match your selected date range."
                      : "No quiz result records returned from the server."}
                  </p>
                  {(appliedFromDate || appliedToDate) && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm waves-effect waves-light px-3"
                      onClick={handleClear}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
